import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, Star, Heart, Calculator, Shield, Compass, 
  Calendar, Hash, Gem, TrendingUp, ArrowRight 
} from 'lucide-react';

const tools = [
  {
    name: 'Free Kundli',
    description: 'Generate detailed birth chart with PDF download',
    icon: Sparkles,
    href: '/kundli',
    color: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    popular: true,
  },
  {
    name: 'Daily Horoscope',
    description: 'Get daily, weekly & monthly predictions',
    icon: Star,
    href: '/horoscope',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-50',
  },
  {
    name: 'Match Making',
    description: 'Kundli Milan with 36 Gunas analysis',
    icon: Heart,
    href: '/matchmaking',
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    popular: true,
  },
  {
    name: 'Panchang',
    description: 'Daily auspicious timings & muhurat',
    icon: Calendar,
    href: '/panchang',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
  },
  {
    name: 'Numerology',
    description: 'Discover your lucky numbers & name analysis',
    icon: Hash,
    href: '/numerology',
    color: 'from-blue-500 to-cyan-600',
    bgColor: 'bg-blue-50',
  },
  {
    name: 'Dosha Analysis',
    description: 'Check Manglik, Kalsarpa & other doshas',
    icon: Shield,
    href: '/dosha',
    color: 'from-red-500 to-orange-600',
    bgColor: 'bg-red-50',
  },
  {
    name: 'Gem Recommendation',
    description: 'Find your perfect lucky gemstone',
    icon: Gem,
    href: '/gem-recommendation',
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-50',
  },
  {
    name: 'Dasha Predictions',
    description: 'Vimshottari Dasha timeline & analysis',
    icon: TrendingUp,
    href: '/dasha',
    color: 'from-indigo-500 to-blue-600',
    bgColor: 'bg-indigo-50',
  },
  {
    name: 'Vastu Compass',
    description: '16 zones guide with remedies',
    icon: Compass,
    href: '/vastu-compass',
    color: 'from-teal-500 to-emerald-600',
    bgColor: 'bg-teal-50',
  },
  {
    name: 'Love Calculator',
    description: 'Check love compatibility percentage',
    icon: Heart,
    href: '/love-calculator',
    color: 'from-rose-500 to-pink-600',
    bgColor: 'bg-rose-50',
  },
];

export const FreeToolsSection = () => {
  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-slate-900 to-slate-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-amber-400 text-sm font-medium mb-4 backdrop-blur">
            <Calculator className="w-4 h-4" />
            100% Free Astrology Tools
          </div>
          <h2 className="font-serif text-3xl lg:text-5xl font-bold text-white mb-4">
            Explore Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">Free Tools</span>
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            Get accurate astrology predictions, generate detailed reports, and discover insights about your life - completely free!
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={tool.href}
                className="group block relative p-5 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full"
              >
                {/* Popular Badge */}
                {tool.popular && (
                  <div className="absolute -top-2 -right-2 px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold">
                    Popular
                  </div>
                )}
                
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <tool.icon className="w-6 h-6 text-white" />
                </div>
                
                {/* Content */}
                <h3 className="font-semibold text-white mb-1 group-hover:text-amber-400 transition-colors">
                  {tool.name}
                </h3>
                <p className="text-slate-400 text-sm line-clamp-2">
                  {tool.description}
                </p>
                
                {/* Arrow */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5 text-amber-400" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-slate-400 mb-4">
            Looking for premium Vastu reports?
          </p>
          <Link
            to="/vastu-tools"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg hover:shadow-amber-500/30 hover:scale-105 transition-all"
          >
            <Compass className="w-5 h-5" />
            Premium Vastu Tools
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
